import React, { memo, useMemo } from 'react'
import tw from 'twin.macro'
import { useSelector } from 'react-redux'
import { TableTabs } from 'src/components/shared/table/table-tabs/TableTabs'
import type { TableTabItem } from 'src/components/shared/table/table-tabs/TableTabs'
import { customerStatusTitles } from 'src/constants/customer/constants'
import { CustomerStatus } from 'src/constants/customer/enums'
import { getCustomers, getCustomersLoading } from 'src/selectors/customer'
import { addS } from 'src/utils/baseUtils'
import { isCustomerInactive } from 'src/utils/customerList'
import { GSIcon } from 'src/components/shared/GSIcon'

interface Props {
  selectedStatus: CustomerStatus
  onChange(status: CustomerStatus): void
}

// Central icons paint via `currentColor`, so colour is set through the text colour, not `fill`.
const selectedIconStyles = tw`text-primary-600`
const iconStyles = tw`text-shades-1000`

/**
 * Active / Inactive tabs above the customer list, the same shape as the dashboard's contractor
 * tabs. Counts are derived from the already-loaded list: the page fetches every customer and
 * filters in memory, so there is no separate counts endpoint to wait for.
 */
const CustomersTabsComponent = ({ selectedStatus, onChange }: Props) => {
  const customers = useSelector(getCustomers)
  const isLoading = useSelector(getCustomersLoading)

  const tabs = useMemo(() => {
    const inactiveCount = customers.filter((customer) => {
      return isCustomerInactive(customer)
    }).length
    const activeCount = customers.length - inactiveCount

    const counts: Record<CustomerStatus, number> = {
      [CustomerStatus.Active]: activeCount,
      [CustomerStatus.Deleted]: inactiveCount,
    }

    // No count until the first load settles — a "0 customers" subtitle during the request would
    // read as an empty account.
    const hasCounts = !isLoading || customers.length > 0

    const items: Array<TableTabItem<CustomerStatus>> = [
      {
        value: CustomerStatus.Active,
        title: customerStatusTitles[CustomerStatus.Active],
        renderIcon: (selected: boolean) => {
          return (
            <GSIcon
              iconName="gs_people"
              size={20}
              css={selected ? selectedIconStyles : iconStyles}
            />
          )
        },
      },
      {
        value: CustomerStatus.Deleted,
        title: customerStatusTitles[CustomerStatus.Deleted],
        renderIcon: (selected: boolean) => {
          return (
            <GSIcon
              iconName="gs_pause"
              size={20}
              css={selected ? selectedIconStyles : iconStyles}
            />
          )
        },
      },
    ]

    return items.map((item) => {
      return {
        ...item,
        dataTestId: `customers-list-tab-${item.value}`,
        subtitle: hasCounts ? `${counts[item.value]} ${addS(counts[item.value], 'customer')}` : '',
      }
    })
  }, [customers, isLoading])

  return <TableTabs<CustomerStatus> tabs={tabs} selectedValue={selectedStatus} onClick={onChange} />
}

export const CustomersTabs = memo(CustomersTabsComponent)
